import React, { useEffect, useMemo, useRef, useState } from "react";

const TranscriptStreamer = ({ transcript, speed = 100 }) => {
  const [renderedChunks, setRenderedChunks] = useState([]);
  const [isLoadingDone, setIsLoadingDone] = useState(false);
  const containerRef = useRef(null);

  // Memoize parsed chunks
  const chunks = useMemo(() => parseTranscript(transcript), [transcript]);

  useEffect(() => {
    let index = 0;
    const interval = setInterval(() => {
      if (index < chunks.length) {
        const nextChunk = chunks[index];
        if (nextChunk) {
          setRenderedChunks((prev) => [...prev, nextChunk]);
        }
        index++;
      } else {
        setIsLoadingDone(true);
        clearInterval(interval);
      }
    }, speed);

    return () => clearInterval(interval);
  }, [chunks, speed]);

  useEffect(() => {
    if (containerRef.current && isLoadingDone) {
      containerRef.current.scrollTo({
        top: containerRef.current.scrollHeight,
        behavior: "smooth",
      });
    }
  }, [renderedChunks, isLoadingDone]);

  useEffect(() => {
    setRenderedChunks([]);
  }, [transcript]);
  

  return (
    <div
      ref={containerRef}
      style={{ height: "100%", overflowY: "auto", scrollbarWidth: "thin" }}
      className="p-4 bg-white rounded font-mono"
    >
      {renderedChunks.map((chunk, idx) =>
        chunk ? (
          <div key={idx} className="mb-4">
            <div className="text-gray-700 text-sm mb-1">
            
              <span className="text-gray-500">[{chunk?.timestamp}]</span>
            </div>
            <div className="text-black">{chunk?.text}</div>
          </div>
        ) : null
      )}
    </div>
  );
};

// Enhanced parser with support for both formats
export function parseTranscript(raw) {

  if (!raw || typeof raw !== "string") return [];

  // If it looks like VTT, parse as VTT
  if (looksLikeVTT(raw)) {
    return parseVTTToChunks(raw);
  }

  // Fallback to your existing line-by-line "spk_X [..-..]:" formats
  const spkChunks = raw
    .split("\n")
    .map((line, index) => {
      // Normalize various dash characters & odd encodings
      const cleanedLine = line
        .replace(/[\u2013\u2014\uFF0D\uFE58\uFE63\u2212]/g, "-") // en/em dashes etc.
        .replace(/â€"|â€"/g, "-") // common mojibake for dashes
        .trim();

      // Format 1: spk_1 [05:30-05:44]: text
      let match = cleanedLine.match(
        /^spk_(\d+)\s+\[(\d{2}:\d{2})-(\d{2}:\d{2})\]:\s*(.*)$/i
      );
      if (match) {
        const speaker = `Speaker ${match[1]}`;
        const timestamp = normalizeTimeLabel(match[2], match[3]);
        const text = match[4].trim();
        return { speaker, timestamp, text };
      }

      // Format 2: spk_0 [2.71s-3.18s]: text
      match = cleanedLine.match(
        /^spk_(\d+)\s+\[(\d+(?:\.\d+)?s)-(\d+(?:\.\d+)?s)\]:\s*(.*)$/i
      );
      if (match) {
        const speaker = `Speaker ${match[1]}`;
        const timestamp = normalizeTimeLabel(match[2], match[3]);
        const text = match[4].trim();
        return { speaker, timestamp, text };
      }

      // NEW Format 3: 0 [00:00-00:05]: text (just number, no spk_ prefix)
      match = cleanedLine.match(
        /^(\d+)\s+\[(\d{2}:\d{2})-(\d{2}:\d{2})\]:\s*(.*)$/i
      );
      if (match) {
        const speaker = `Speaker ${match[1]}`;
        const timestamp = normalizeTimeLabel(match[2], match[3]);
        const text = match[4].trim();
        return { speaker, timestamp, text };
      }

      // Flexible pattern: spk_2 [anything]: text
      match = cleanedLine.match(/^spk_(\d+)\s+\[([^\]]+)\]:\s*(.*)$/i);
      if (match) {
        const speaker = `Speaker ${match[1]}`;
        const timestamp = match[2];
        const text = match[3].trim();
        return { speaker, timestamp, text };
      }

      // NEW Flexible pattern: 2 [anything]: text (just number, no spk_ prefix)
      match = cleanedLine.match(/^(\d+)\s+\[([^\]]+)\]:\s*(.*)$/i);
      if (match) {
        const speaker = `Speaker ${match[1]}`;
        const timestamp = match[2];
        const text = match[3].trim();
        return { speaker, timestamp, text };
      }

      return null;
    })
    .filter(Boolean);

  // Plain-text fallback: if no structured chunks matched, split on newlines
  // and emit one chunk per non-blank line so plain .txt transcripts render.
  if (spkChunks.length === 0) {
    return raw
      .split("\n")
      .map((line) => line.trim())
      .filter((line) => line.length > 0)
      .map((line) => ({ speaker: null, timestamp: null, text: line }));
  }

  return spkChunks;
}

function stripHtmlTags(s = "") {
  return s.replace(/<\/?[^>]+>/g, "");
}

function pickSpeakerFromVTTCue(line = "") {
  // Match <v Speaker Name> ... or <v-> (anonymous)
  const m = line.match(/<v(?:[^>]*)>(.*)$/i);
  if (!m) return null;
  // If tag has attribute like <v Foo>, first token before text might be speaker,
  // but in practice VTT often keeps speaker right after <v ...> start of line.
  // We'll treat whatever precedes the first ":" as the speaker (if present).
  const rest = m[1].trim();
  const parts = rest.split(":");
  if (parts.length > 1) {
    const sp = parts[0].trim();
    return sp || null;
  }
  return null;
}

function normalizeTimeLabel(a, b) {
  // Accept "hh:mm:ss.mmm" | "mm:ss.mmm" | "mm:ss" | "s.mmm" | "s"
  const toMs = (t) => {
    if (!t) return 0;
    const ts = t.trim();
    if (/^\d+(\.\d+)?s$/i.test(ts)) {
      // "2.71s"
      return Math.round(parseFloat(ts) * 1000);
    }
    // "hh:mm:ss.mmm" or "mm:ss.mmm" or "mm:ss"
    const parts = ts.split(":").map(Number);
    let ms = 0;
    if (parts.length === 3) {
      // hh:mm:ss(.mmm)
      const [hh, mm, ssFrac] = parts;
      const ss = Math.floor(ssFrac);
      const frac = String(ssFrac).includes(".")
        ? parseFloat("0." + String(ssFrac).split(".")[1]) * 1000
        : 0;
      ms = ((hh * 60 + mm) * 60 + ss) * 1000 + frac;
    } else if (parts.length === 2) {
      const [mm, ssFrac] = parts;
      const ss = Math.floor(ssFrac);
      const frac = String(ssFrac).includes(".")
        ? parseFloat("0." + String(ssFrac).split(".")[1]) * 1000
        : 0;
      ms = (mm * 60 + ss) * 1000 + frac;
    } else if (/^\d+(\.\d+)?$/.test(ts)) {
      // plain seconds number
      ms = Math.round(parseFloat(ts) * 1000);
    }
    return ms;
  };

  const msA = toMs(a);
  const msB = toMs(b);

  const toMMSS = (ms) => {
    const totalSec = Math.max(0, Math.floor(ms / 1000));
    const m = Math.floor(totalSec / 60);
    const s = totalSec % 60;
    return `${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
  };

  return `${toMMSS(msA)} - ${toMMSS(msB)}`;
}

function parseVTTToChunks(raw) {
  // Remove BOM & WEBVTT header and NOTE blocks
  let text = raw.replace(/^\uFEFF/, "");
  text = text.replace(/^WEBVTT[^\n]*\n?/i, "");
  text = text.replace(/^NOTE[\s\S]*?(?:\n{2,}|$)/gim, "");

  const lines = text.split(/\r?\n/);
  const chunks = [];
  let i = 0;

  while (i < lines.length) {
    let line = lines[i].trim();

    // Skip empty or pure index lines
    if (!line || /^\d+$/.test(line)) {
      i++;
      continue;
    }

    // Timestamp cue line: "00:00:01.000 --> 00:00:04.000 ..."
    const ts = line.match(
      /^(\d{1,2}:\d{2}(?::\d{2})?(?:\.\d{3})?)\s*-->\s*(\d{1,2}:\d{2}(?::\d{2})?(?:\.\d{3})?)/
    );
    if (ts) {
      const start = ts[1];
      const end = ts[2];
      i++;

      // Collect following non-empty text lines until blank or next cue
      const textLines = [];
      while (i < lines.length && lines[i].trim() !== "") {
        textLines.push(lines[i]);
        i++;
      }

      if (textLines.length) {
        const combined = textLines.join(" ").trim();

        // Try to extract speaker from <v ...> if present
        const speakerTag = pickSpeakerFromVTTCue(textLines[0]);
        const speaker = speakerTag ? speakerTag : "Speaker 1";

        // Remove HTML tags, <v> tags, and collapse spaces
        const cleaned = stripHtmlTags(
          combined.replace(/<v[^>]*>/gi, "")
        ).replace(/\s+/g, " ").trim();

        if (cleaned) {
          chunks.push({
            speaker,
            timestamp: normalizeTimeLabel(start, end),
            text: cleaned,
          });
        }
      }
      // Skip the empty line after cue (if any)
      i++;
      continue;
    }

    // If we hit here, it's not a cue line — advance
    i++;
  }

  return chunks;
}

function looksLikeVTT(raw) {
  if (!raw) return false;
  if (/^\uFEFF?WEBVTT/i.test(raw)) return true;
  // Heuristic: many " --> " cue lines means VTT-ish
  const arrowCount = (raw.match(/-->/g) || []).length;
  return arrowCount >= 1; // at least one cue
}

export default TranscriptStreamer;